import { Service, Container } from "typedi";
import { HttpException } from "../../../utils/exceptions/httpException";
import { TFunction } from "i18next";
import { status } from "../../../utils/helpers/api.responses";
import mongoose, { Types } from "mongoose";
import User from "../../../Modules/Auth/models/auth.model";
import { AvailableStatus, AvailableStatusEnum, IAvailability } from "../interfaces/availability.interface";
import { RolesEnum } from "../../../Modules/Auth/interfaces/auth.interface";
import Availability from "../models/availability.model";
import AllocationRequest from "../../../Modules/allocationRequest/models/allocationRequest.model";
import Labour from "../../../Modules/Labour/models/labour.model";
import { NotificationService } from "../../../Modules/Notification/services/notification.service";
import { AllocationRequestStatusEnum } from "../../../Modules/allocationRequest/interfaces/allocationRequest.interface";
import { aws } from "../../../config/index";
import Job from "../../../Modules/jobManagement/models/job.model";
import statusTicket from '../../Labour/constant/status.json'

@Service()
export class AvailabilityService {
    public notification = Container.get(NotificationService);

    private async sendAvailabilityNotification(
        action: string,
        data: { from: Types.ObjectId; to: Types.ObjectId | Types.ObjectId[]; entityData: any },
        t: TFunction
    ) {
        try {
            await this.notification.sendNotification('availability', action, data, t);
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, "Error in sending Notification");
        }
    }

    public async setAvailabilityStatus(
        data: {
            userId: string;
            startDate: Date;
            endDate: Date;
            availabilityStatus: AvailableStatus;
            jobId: string;
            createdBy: string;
            holiday?: string;
        },
        t: TFunction
    ) {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction({
                readConcern: { level: 'snapshot' },
                writeConcern: { w: 'majority' },
                maxCommitTimeMS: 300000,
            });

            const {
                userId,
                startDate,
                endDate,
                availabilityStatus,
                jobId,
                holiday,
                createdBy,
            } = data;

            if (!userId || !createdBy) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'userId, createdBy' }));
            }

            const lowerCaseStatus = availabilityStatus?.toString().trim().toLowerCase();
            if (!lowerCaseStatus) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'availabilityStatus' }));
            }
            if (availabilityStatus.toLowerCase() === AvailableStatusEnum.UNAVAILABLE.toLowerCase() && !jobId) {
                throw new HttpException(status.BadRequest, 'JobId is required')
            }

            const validStatuses = [
                AvailableStatusEnum.UNAVAILABLE,
                AvailableStatusEnum.HOLIDAY
            ].map(s => s.toLowerCase());

            if (!validStatuses.includes(lowerCaseStatus)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'availabilityStatus' }));
            }

            const enumMatch = Object.values(AvailableStatusEnum).find(
                e => e.toLowerCase() === lowerCaseStatus
            );
            if (!enumMatch) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'availabilityStatus' }));
            }

            if (!startDate || !endDate) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'startDate, endDate' }));
            }

            const start = new Date(startDate);
            const end = new Date(endDate);
            if (end <= start) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'endDate must be after startDate' }));
            }

            const user = await User.findById(userId).populate('roleId').session(session).lean();
            if (!user || (user.roleId as any).name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase()) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: t('User.labour') }));
            }

            const createdByUser = await User.findById(createdBy)
                .populate('roleId')
                .select('firstName lastName email')
                .session(session)
                .lean();

            if (!createdByUser) {
                throw new HttpException(status.BadRequest, t('General.not_found', { field: t('User.user') }));
            }

            const createdByRole = (createdByUser.roleId as any).name.toLowerCase();
            if (
                ![RolesEnum.ADMIN.toLowerCase(), RolesEnum.STAFF.toLowerCase(), RolesEnum.LABOUR.toLowerCase()].includes(createdByRole) ||
                (createdByRole === RolesEnum.LABOUR.toLowerCase() && userId.toString() !== createdBy.toString())
            ) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            const labour = await Labour.findOne({ userId, isDeleted: false }).session(session).lean();
            if (!labour) {
                throw new HttpException(status.BadRequest, t('General.not_found', { field: t('Labour.labour') }));
            }

            let companyName, location;
            if (jobId) {
                const job = await Job.findById(jobId).session(session).lean();
                if (!job) {
                    throw new HttpException(status.BadRequest, t('General.not_found', { field: 'jobId' }));
                }
                companyName = job.companyName;
                location = job.location;
            }

            const acceptedRequests = await AllocationRequest.find({
                userId,
                startDate: { $lte: endDate },
                endDate: { $gte: startDate },
                status: { $regex: `^${AllocationRequestStatusEnum.ACCEPTED}`, $options: 'i' },
                isDeleted: false,
            }).session(session).lean();

            if (acceptedRequests.length > 0) {
                const jobIds = acceptedRequests.map(r => r.jobId);
                const jobs = await Job.find({ _id: { $in: jobIds } }).session(session).lean();
                const conflictingJob = jobs.find(j => j.status === 'Open');
                if (conflictingJob) {
                    throw new HttpException(status.BadRequest, `Cannot set unavailable: Labour has an accepted job (Job ID: ${conflictingJob._id})`);
                }
            }

            const existingAvailability = await Availability.findOne({
                userId,
                startDate: { $lte: endDate },
                endDate: { $gte: startDate },
                status: lowerCaseStatus,
                isDeleted: false,
            }).session(session).lean();

            if (existingAvailability) {
                throw new HttpException(status.BadRequest, t('General.already_exist', { field: t('Availability.availability') }));
            }

            const availability = new Availability({
                userId,
                startDate,
                endDate,
                status: enumMatch as AvailableStatus,
                jobId: jobId ? new Types.ObjectId(jobId) : null,
                createdBy: new Types.ObjectId(createdBy),
                updatedBy: new Types.ObjectId(createdBy),
                holidayNotes: lowerCaseStatus === AvailableStatusEnum.HOLIDAY.toLowerCase() ? holiday?.trim() : null,
            });

            availability.$session(session);
            await availability.save();

            await Labour.updateOne(
                { userId, isDeleted: false },
                {
                    $push: { availability: availability._id },
                    availability_status: enumMatch as AvailableStatus,
                },
                { session }
            );




            const existingLabour = await Labour.findOne({ userId, isDeleted: false }).session(session);
            if (existingLabour) {
                const [validAvailabilityIds, validAllocationRequestIds] = await Promise.all([
                    Availability.find({
                        _id: { $in: existingLabour.availability },
                        isDeleted: false
                    }).distinct('_id').session(session),
                    AllocationRequest.find({
                        _id: { $in: existingLabour.allocationRequest },
                        isDeleted: false
                    }).distinct('_id').session(session),
                ]);

                const invalidAvailabilityIds = existingLabour.availability.filter(
                    id => !validAvailabilityIds.map(String).includes(id.toString())
                );
                const invalidAllocationRequestIds = existingLabour.allocationRequest.filter(
                    id => !validAllocationRequestIds.map(String).includes(id.toString())
                );

                const update: any = {};
                if (invalidAvailabilityIds.length > 0) {
                    update.$pull = { ...(update.$pull || {}), availability: { $in: invalidAvailabilityIds } };
                }
                if (invalidAllocationRequestIds.length > 0) {
                    update.$pull = { ...(update.$pull || {}), allocationRequest: { $in: invalidAllocationRequestIds } };
                }

                if (Object.keys(update).length > 0) {
                    await Labour.updateOne({ _id: existingLabour._id }, update, { session });
                }
            }

            await session.commitTransaction();
            transactionCommitted = true
            // await this.sendAvailabilityNotification(
            //     'newAvailability',
            //     {
            //         from: new Types.ObjectId(createdBy),
            //         to: new Types.ObjectId(userId),
            //         entityData: {
            //             userId,
            //             availabilityId: availability._id,
            //             startDate,
            //             endDate,
            //             status: lowerCaseStatus,
            //             companyName,
            //             location,
            //             holidayNotes: lowerCaseStatus === AvailableStatusEnum.HOLIDAY.toLowerCase() ? holiday : null,
            //             firstName: user.firstName,
            //             lastName: user.lastName,
            //             labourId: labour.employee_id || 'N/A',
            //         },
            //     },
            //     t
            // );
            // if (createdByRole === RolesEnum.STAFF.toLowerCase()) {
            //     const adminUsers = await User.find({ isDeleted: false })
            //         .populate({
            //             path: 'roleId',
            //             match: { name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } },
            //             select: 'name',
            //         })
            //         .select('_id')
            //         .lean();

            //     const adminIds = adminUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            //     if (adminIds.length > 0) {
            //         await this.sendAvailabilityNotification(
            //             'newAvailability',
            //             {
            //                 from: new Types.ObjectId(createdBy),
            //                 to: adminIds,
            //                 entityData: {
            //                     userId,
            //                     startDate,
            //                     endDate,
            //                     status: lowerCaseStatus,
            //                     companyName,
            //                     holidayNotes: lowerCaseStatus === AvailableStatusEnum.HOLIDAY.toLowerCase() ? holiday : null,
            //                     location,
            //                     firstName: user.firstName,
            //                     lastName: user.lastName,
            //                     responderName: createdByUser.firstName || createdByUser.email,
            //                     labourId: labour.employee_id || 'N/A',
            //                     responseNotes: `Labourer marked ${lowerCaseStatus} by ${createdByUser.firstName || createdByUser.email}`,
            //                 },
            //             },
            //             t
            //         );
            //     }
            // }

            return {
                _id: availability._id,
                status: lowerCaseStatus,
                startDate,
                endDate,
                companyName,
                jobId,
                location,
                message: `Availability status successfully set to ${lowerCaseStatus}`,
            };

        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            console.error('Transaction aborted due to error:', error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Availability.availability') }));
        } finally {
            await session.endSession();
        }
    }


    public async getAvailabilityById(id: string, t: TFunction) {
        try {

            const availability = await Availability.findOne({ _id: id, isDeleted: false })
                .populate('userId', 'firstName lastName email phone countryCode')
                .lean();
            if (!availability) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Availability.availability') }));
            }
            const labour = await Labour.findOne({ userId: availability.userId, isDeleted: false })
                .select('employee_id skills')
                .lean();
            return { ...availability, labour: labour || {} };
        } catch (error) {
            if (error instanceof HttpException) throw error;
            console.error('Error in getAvailabilityById:', error);
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Availability.availability') }));
        }
    }

    public async getAllAvailabilityStatuses(
        filters: {
            availability_status?: string;
            occupation?: string[];
            tickets?: string[];
            startDate?: Date;
            endDate?: Date;
            isPartial?: boolean;
            search?: string;
            [key: string]: any;
        },
        page: number,
        limit: number,
        sort: any,
        t: TFunction
    ): Promise<any> {
        try {
            const skip = (page - 1) * limit;
            const {
                startDate,
                endDate,
                isPartial = false,
                availability_status,
                occupation,
                tickets,
                search,
                ...otherFilters
            } = filters;
            const utcStartDate = new Date(startDate.getTime());
            utcStartDate.setHours(0, 0, 0, 0);

            const utcEndDate = new Date(endDate.getTime());
            utcEndDate.setHours(23, 59, 59, 999);

            const labourQuery: any = { isDeleted: false };

            if (availability_status) {
                labourQuery.availability_status = { $regex: `^${availability_status}$`, $options: 'i' };
            }

            if (occupation && Array.isArray(occupation) && occupation.length > 0) {
                const occupationCodes = occupation
                    .map(occName => {
                        return Object.keys(statusTicket.Occupation).find(key => {
                            const value = statusTicket.Occupation[key];
                            return typeof value === "string" && value.toLowerCase() === occName.toLowerCase();
                        });
                    })
                    .filter(Boolean);


                if (occupationCodes.length > 0) {
                    labourQuery.occupation = { $in: occupationCodes };
                }
            }

            if (tickets && tickets.length > 0) {
                const ticketCodes = tickets
                    .map(ticketName => {
                        const code = Object.keys(statusTicket.tickets).find(
                            key => statusTicket.tickets[key].toLowerCase() === ticketName.toLowerCase()
                        );
                        return code;
                    })
                    .filter(Boolean);

                if (ticketCodes.length > 0) {
                    labourQuery.tickets = {
                        $in: ticketCodes
                    };
                }
            }
            console.log("tickets", tickets)

            Object.assign(labourQuery, otherFilters);

            const [labours, unavailableRecords] = await Promise.all([
                Labour.find(labourQuery)
                    .select('userId employee_id skills tickets sitesInductedOn occupation availability_status')
                    .populate({
                        path: 'userId',
                        select: 'firstName lastName email profileImage phone countryCode',
                        populate: {
                            path: 'profileImage',
                            model: 'Attachment',
                            select: 'key filename fileSize',
                        },
                    })
                    .sort(sort)
                    .lean(),

                Availability.find({
                    isDeleted: false,
                    status: { $regex: `^${AvailableStatusEnum.UNAVAILABLE}`, $options: 'i' },
                    $or: [
                        { startDate: { $lte: utcEndDate }, $or: [{ endDate: { $gte: utcStartDate } }, { endDate: null }] },
                        { endDate: { $gte: utcStartDate } },
                    ],
                })
                    .populate('userId', '_id')
                    .lean(),
            ]);

            const unavailableUserIds = new Set(unavailableRecords.map(r => r.userId._id.toString()));
            const userAvailabilityMap = new Map<string, { _id: string; periods: { startDate: Date | null; endDate: Date | null }[] }>();

            unavailableRecords.forEach(r => {
                const userId = r.userId._id.toString();
                if (!userAvailabilityMap.has(userId)) {
                    userAvailabilityMap.set(userId, { _id: r._id.toString(), periods: [] });
                }
                userAvailabilityMap.get(userId)!.periods.push({
                    startDate: r.startDate ? new Date(r.startDate) : null,
                    endDate: r.endDate ? new Date(r.endDate) : null,
                });
            });

            let filteredLabours = labours;
            if (search && typeof search === 'string') {
                const regex = new RegExp(search, 'i');
                filteredLabours = labours.filter(l => {
                    const user = l.userId as any;
                    return regex.test(user.firstName + ' ' + user.lastName);
                });
            }

            const allAvailableLabourers = filteredLabours
                .map(l => {
                    const userId = l.userId._id.toString();
                    const availabilityData = userAvailabilityMap.get(userId);

                    if (!availabilityData || !availabilityData.periods.length) {
                        return {
                            _id: userId,
                            userId: l.userId._id,
                            firstName: (l.userId as any).firstName,
                            lastName: (l.userId as any).lastName,
                            email: (l.userId as any).email,
                            skills: l.skills,
                            occupation: l.occupation ?? [],
                            tickets: Array.isArray(l.tickets)
                                ? l.tickets.map(ticketCode => statusTicket.tickets[ticketCode] ?? ticketCode)
                                : [],
                            availability_status: l.availability_status,
                            startDate: utcStartDate.toISOString(),
                            endDate: utcEndDate.toISOString(),
                            isPartial: false,
                            profileImage: (l.userId as any).profileImage
                                ? {
                                    _id: (l.userId as any).profileImage._id.toString(),
                                    filename: (l.userId as any).profileImage.filename || null,
                                    url: (l.userId as any).profileImage.key
                                        ? `${aws.S3_BASE_URL}/${(l.userId as any).profileImage.key}`
                                        : null,
                                    fileSize: (l.userId as any).profileImage.fileSize
                                }
                                : null,
                        };
                    }

                    const unavailablePeriods = availabilityData.periods;
                    let isFullyUnavailable = false;
                    let availableStart = utcStartDate;
                    let availableEnd = utcEndDate;

                    for (const period of unavailablePeriods) {
                        const periodStart = period.startDate || new Date(0);
                        const periodEnd = period.endDate || new Date(8640000000000000);

                        if (periodStart <= utcStartDate && periodEnd >= utcEndDate) {
                            isFullyUnavailable = true;
                            break;
                        }

                        if (periodStart <= utcEndDate && periodEnd >= utcStartDate) {
                            if (periodStart > utcStartDate && periodStart < availableEnd) {
                                availableEnd = new Date(periodStart.getTime() - 1);
                            }
                            if (periodEnd < utcEndDate && periodEnd > availableStart) {
                                availableStart = new Date(periodEnd.getTime() + 1);
                            }
                        }
                    }

                    if (isFullyUnavailable || availableStart > availableEnd) return null;

                    const isPartialAvailability = availableStart.getTime() !== utcStartDate.getTime()
                        || availableEnd.getTime() !== utcEndDate.getTime();

                    if (!isPartial && isPartialAvailability) return null;

                    return {
                        _id: availabilityData._id,
                        userId: l.userId._id,
                        firstName: (l.userId as any).firstName,
                        lastName: (l.userId as any).lastName,
                        email: (l.userId as any).email,
                        occupation: l.occupation ?? [],
                        tickets: Array.isArray(l.tickets)
                            ? l.tickets.map(ticketCode => statusTicket.tickets[ticketCode] ?? ticketCode)
                            : [],
                        skills: l.skills,
                        availability_status: l.availability_status,
                        startDate: availableStart.toISOString(),
                        endDate: availableEnd.toISOString(),
                        isPartial: isPartialAvailability,
                        profileImage: (l.userId as any).profileImage
                            ? {
                                _id: (l.userId as any).profileImage._id.toString(),
                                filename: (l.userId as any).profileImage.filename || null,
                                url: (l.userId as any).profileImage.key
                                    ? `${aws.S3_BASE_URL}/${(l.userId as any).profileImage.key}`
                                    : null,
                                fileSize: (l.userId as any).profileImage.fileSize
                            }
                            : null,
                    };
                })
                .filter((l): l is NonNullable<typeof l> => l !== null);

            const total = allAvailableLabourers.length;
            const totalPage = Math.ceil(total / limit);
            const paginatedLabourers = allAvailableLabourers.slice(skip, skip + limit);

            return {
                data: paginatedLabourers,
                total,
                page,
                totalPage,
            };
        } catch (error) {
            console.error('Error in getAllAvailabilityStatuses:', error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Availability.availability') }));
        }
    }

    public async updateAvailability(id: string, updates: Partial<IAvailability>, t: TFunction) {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();
            const availability = await Availability.findOne({ _id: id, isDeleted: false })
                .populate('userId', 'firstName lastName email')
                .populate('jobId', 'companyName location')
                .session(session);
            const isSelf = availability.userId._id.toString() === updates.updatedBy.toString()
            const updatedByUser = await User.findById(updates.updatedBy)
                .populate('roleId')
                .select('firstName lastName email')
                .session(session)
                .lean();

            if (updates.holiday && !updates.holidayNotes) {
                updates.holidayNotes = updates.holiday;
                delete updates.holiday;
            }
            const updateFields = Object.keys(updates).filter(key => key !== 'updatedBy' && key !== 'updatedAt');

            const labour = await Labour.findOne({ userId: availability.userId, isDeleted: false })
                .select('employee_id')
                .session(session)
                .lean();
            if (!availability) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Availability.availability') }));
            }
            if (!updatedByUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.updatedBy') }));
            }

            const updatedByRole = (updatedByUser.roleId as any).name.toLowerCase();
            if (!isSelf && ![RolesEnum.ADMIN.toLowerCase(), RolesEnum.STAFF.toLowerCase()].includes(updatedByRole)) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            if (updateFields.length === 0) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'at least one update field' }));
            }

            const updateData: Partial<IAvailability> = {};
            for (const key of updateFields) {
                if (updates[key] !== undefined) {
                    if (key === 'status' && typeof updates[key] === 'string') {
                        const matchedEnum = Object.values(AvailableStatusEnum).find(
                            val => val.toLowerCase() === (updates[key] as string).toLowerCase()
                        );

                        if (!matchedEnum) {
                            throw new HttpException(status.BadRequest, t('General.invalid', { field: 'status' }));
                        }

                        updateData[key] = matchedEnum;
                    } else {
                        updateData[key] = updates[key];
                    }

                }
            }
            if (
                updateData.status &&
                !Object.values(AvailableStatusEnum).some(
                    val => val.toLowerCase() === updateData.status.toLowerCase()
                )
            ) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'status' }));
            }

            const isUpdatingDates = updateFields.includes('startDate') || updateFields.includes('endDate');
            const start = updateData.startDate ?? availability.startDate;
            const end = updateData.endDate ?? availability.endDate;

            if (end < start) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'endDate must be after startDate' }));
            }

            const willBeUnavailable =
                (updateData.status ?? availability.status).toLowerCase() === AvailableStatusEnum.UNAVAILABLE.toLowerCase();

            if (isUpdatingDates && willBeUnavailable) {
                const existingAvailability = await Availability.findOne({
                    userId: updateData.userId || availability.userId,
                    startDate: { $lte: end },
                    endDate: { $gte: start },
                    status: { $regex: `^${AvailableStatusEnum.UNAVAILABLE}`, $options: 'i' },
                    isDeleted: false,
                    _id: { $ne: id },
                }).session(session).lean();

                if (existingAvailability) {
                    throw new HttpException(status.BadRequest, t('General.already_exist', { field: t('Availability.availability') }));
                }
            }



            if (updateData.userId && updateData.userId.toString() !== availability.userId.toString()) {
                const user = await User.findById(updateData.userId).populate('roleId').session(session).lean();
                if (!user || (user.roleId as any).name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase()) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: t('User.labour') }));
                }
            }
            if (updateData.jobId && updateData.jobId.toString() !== (availability.jobId?.toString() ?? '')) {
                const job = await Job.findById(updateData.jobId).lean();
                if (!job || job.isDeleted || job.status === 'Closed') {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: t('Job.job') }));
                }
                await Promise.all([
                    Job.updateOne(
                        { _id: availability.jobId },
                        { $inc: { allotted: -1, open: 1 } },
                        { session }
                    ),
                    Job.updateOne(
                        { _id: updateData.jobId },
                        { $inc: { allotted: 1, open: -1 } },
                        { session }
                    ),
                ]);
            }

            updateData.updatedBy = updates.updatedBy;
            updateData.updatedAt = new Date();

            Object.assign(availability, updateData);
            availability.$session(session)

            await availability.save();
            if (updateData.status) {
                const unavailableCount = await Availability.countDocuments({
                    userId: availability.userId,
                    status: { $regex: `^${AvailableStatusEnum.UNAVAILABLE}`, $options: 'i' },
                    isDeleted: false,
                }).session(session);
                await Labour.updateOne(
                    { userId: availability.userId, isDeleted: false },
                    {
                        availability_status: unavailableCount > 0 ? AvailableStatusEnum.UNAVAILABLE.toLowerCase() : AvailableStatusEnum.AVAILABLE.toLowerCase(),
                    },
                    { session }
                );
            }

            await session.commitTransaction();
            transactionCommitted = true
            // await this.sendAvailabilityNotification(
            //     'updateAvailability',
            //     {
            //         from: new Types.ObjectId(updates.updatedBy),
            //         to: availability.userId,
            //         entityData: {
            //             userId: availability.userId,
            //             availabilityId: availability._id,
            //             startDate: updateData.startDate ?? availability.startDate,
            //             endDate: updateData.endDate ?? availability.endDate,
            //             status: updateData.status ?? availability.status,
            //             updatedFields: updateFields.join(', '),
            //             firstName: (availability.userId as any).firstName,
            //             lastName: (availability.userId as any).lastName,
            //             responderName: updatedByUser.firstName || updatedByUser.email,
            //             responseNotes: '',
            //             holidayNotes: availability.holidayNotes || '',
            //             companyName: (availability.jobId as any)?.companyName || 'N/A',
            //             location: (availability.jobId as any)?.location || 'Unknown',
            //             labourId: labour?.employee_id || 'N/A',
            //         },
            //     },
            //     t
            // );


            // if (updatedByRole === RolesEnum.STAFF.toLowerCase()) {
            //     const adminUsers = await User.find({ isDeleted: false })
            //         .populate({
            //             path: 'roleId',
            //             match: { name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } },
            //             select: 'name',
            //         })
            //         .select('_id')
            //         .lean();
            //     const adminIds = adminUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            //     if (adminIds.length > 0) {
            //         await this.sendAvailabilityNotification(
            //             'updateAvailability',
            //             {
            //                 from: new Types.ObjectId(updates.updatedBy),
            //                 to: adminIds,
            //                 entityData: {
            //                     userId: availability.userId,
            //                     availabilityId: availability._id,
            //                     startDate: updateData.startDate ?? availability.startDate,
            //                     endDate: updateData.endDate ?? availability.endDate,
            //                     status: updateData.status ?? availability.status,
            //                     updatedFields: updateFields.join(', '),
            //                     firstName: (availability.userId as any).firstName,
            //                     lastName: (availability.userId as any).lastName,
            //                     responderName: updatedByUser.firstName || updatedByUser.email,
            //                     responseNotes: '',
            //                     holidayNotes: availability.holidayNotes || '',
            //                     companyName: (availability.jobId as any)?.companyName || 'N/A',
            //                     location: (availability.jobId as any)?.location || 'Unknown',
            //                     labourId: labour?.employee_id || 'N/A',
            //                 },
            //             },
            //             t
            //         );
            //     }
            // }

            return availability;
        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            console.log(error)
            if (error instanceof HttpException) throw error;
            console.error('Error in updateAvailability:', error);
            throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Availability.unavailability') }));
        } finally {
            await session.endSession();
        }
    }

    public async deleteMultipleAvailabilities(ids: string[], updatedBy: string, t: TFunction): Promise<{ deleted: number }> {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();

            const availabilities = await Availability.find({ _id: { $in: ids }, isDeleted: false })
                .populate('userId', 'firstName lastName email _id')
                .populate('jobId', 'companyName location')
                .session(session)
            const updatedByUser = await User.findById(updatedBy)
                .populate('roleId')
                .select('firstName lastName email')
                .session(session)
                .lean()

            if (!availabilities.length || availabilities.length === 0) {
                await session.commitTransaction();
                return { deleted: 0 };
            }

            if (!updatedByUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.updatedBy') }));
            }

            const updatedByRole = (updatedByUser.roleId as any).name.toLowerCase();
            if (![RolesEnum.ADMIN.toLowerCase(), RolesEnum.STAFF.toLowerCase(), RolesEnum.LABOUR.toLowerCase()].map(r => r.toLowerCase()).includes(updatedByRole)) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            if (updatedByRole === RolesEnum.LABOUR.toLowerCase()) {
                const invalidEntries = availabilities.filter(a =>
                    (a.userId as any)._id.toString() !== updatedByUser._id.toString()
                );

                if (invalidEntries.length > 0) {
                    throw new HttpException(status.Forbidden, t('Role.permission_denied'));
                }
            }
            const bulkOps = availabilities.map(availability => ({
                updateOne: {
                    filter: { _id: availability._id },
                    update: {
                        $set: {
                            isDeleted: true,
                            deletedAt: new Date(),
                            updatedBy: new Types.ObjectId(updatedBy),
                        },
                    },
                },
            }));
            await Availability.bulkWrite(bulkOps, { session });
            const userIds = [...new Set(availabilities.map(a => {
                const user = a.userId as any;
                return user?._id?.toString();
            }).filter(id => id))];


            const labours = await Labour.find(
                {
                    userId: { $in: userIds.map(id => new Types.ObjectId(id)) },
                    isDeleted: false
                },
                {
                    userId: 1,
                    employee_id: 1
                }
            )
                .session(session)
                .lean();


            const labourMap = new Map(
                labours
                    .filter(l => l.userId)
                    .map(l => [l.userId.toString(), l.employee_id])
            );

            // await Promise.all(
            //     availabilities.map(async availability => {
            //         const user = availability.userId as any;
            //         if (!user?._id) {
            //             console.warn(`Skipping notification for availability ${availability._id}: userId is not populated`);
            //             return;
            //         }
            //         await Labour.cleanupReferences(user._id.toString(), availability._id.toString(), 'Availability', { session });

            //         await this.sendAvailabilityNotification(
            //             'deleteAvailability',
            //             {
            //                 from: new Types.ObjectId(updatedBy),
            //                 to: user._id,
            //                 entityData: {
            //                     userId: user._id,
            //                     availabilityId: availability._id,
            //                     startDate: availability.startDate?.toISOString() || '',
            //                     endDate: availability.endDate?.toISOString() || '',
            //                     status: availability.status,
            //                     labourId: labourMap.get(user._id.toString()) || '',
            //                     firstName: user.firstName || 'Unknown',
            //                     lastName: user.lastName || 'User',
            //                     holidayNotes: availability.holidayNotes || '',
            //                     companyName: (availability.jobId as any)?.companyName || 'N/A',
            //                     location: (availability.jobId as any)?.location || 'Unknown',
            //                 },
            //             },
            //             t
            //         );
            //     })
            // );

            const updatePromises = userIds.map(async userId => {
                const unavailableCount = await Availability.countDocuments({
                    userId,
                    status: { $regex: `^${AvailableStatusEnum.UNAVAILABLE}`, $options: 'i' },
                    isDeleted: false,
                }).session(session);
                if (unavailableCount === 0) {
                    await Labour.updateOne(
                        { userId, isDeleted: false },
                        { availability_status: AvailableStatusEnum.AVAILABLE },
                        { session }
                    );
                }
            });
            await Promise.all(updatePromises);

            await session.commitTransaction();
            transactionCommitted = true
            // if (updatedByRole === RolesEnum.STAFF.toLowerCase()) {
            //     const adminUsers = await User.find({ isDeleted: false })
            //         .populate({
            //             path: 'roleId',
            //             match: { name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } },
            //             select: 'name',
            //         })
            //         .select('_id')
            //         .lean();
            //     const adminIds = adminUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            //     if (adminIds.length > 0) {
            //         await Promise.all(
            //             availabilities.map(async availability => {
            //                 const user = availability.userId as any;
            //                 if (!user?._id) {
            //                     console.warn(`Skipping admin notification for availability ${availability._id}: userId is not populated`);
            //                     return;
            //                 }
            //                 await this.sendAvailabilityNotification(
            //                     'deleteAvailability',
            //                     {
            //                         from: new Types.ObjectId(updatedBy),
            //                         to: adminIds,
            //                         entityData: {
            //                             userId: user._id,
            //                             availabilityId: availability._id,
            //                             startDate: availability.startDate?.toISOString() || '',
            //                             endDate: availability.endDate?.toISOString() || '',
            //                             status: availability.status,
            //                             labourId: labourMap.get(user._id.toString()) || '',
            //                             firstName: user.firstName || 'Unknown',
            //                             lastName: user.lastName || 'User',
            //                             responderName: updatedByUser.firstName || updatedByUser.email,
            //                             responseNotes: '',
            //                             holidayNotes: availability.holidayNotes || '',
            //                             companyName: (availability.jobId as any)?.companyName || 'N/A',
            //                             location: (availability.jobId as any)?.location || 'Unknown',
            //                         },
            //                     },
            //                     t
            //                 );
            //             })
            //         );
            //     }
            // }
            return { deleted: availabilities.length };
        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }

            if (error instanceof HttpException) throw error;
            console.error('Error in deleteMultipleAvailabilities:', error);
            throw new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('Availability.availability') }));
        } finally {
            await session.endSession();
        }
    }


    public async getCalendarData(
        month: number,
        year: number,
        date: string | undefined,
        availableStatus: string | undefined,
        t: TFunction
    ) {
        try {
            if (month < 1 || month > 12 || year < 1970 || year > 2100) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'month or year' }));
            }

            const labourUsers = await User.find({ isDeleted: false })
                .populate({
                    path: 'roleId',
                    match: { name: { $regex: `^${RolesEnum.LABOUR.toLowerCase()}$`, $options: 'i' } },
                    select: 'name',
                })
                .select('_id firstName lastName email phone countryCode')
                .lean();

            const validLabourUsers = labourUsers.filter(user => user.roleId);
            const labourUserIds = validLabourUsers.map(user => user._id.toString());

            if (!labourUserIds.length) {
                return {
                    data: { available: [], unavailable: [] },
                    availableCount: 0,
                    unavailableCount: 0
                };
            }

            let startDate = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0));
            let endDate = new Date(Date.UTC(year, month - 1, 31, 23, 59, 59));

            if (date) {
                const specificDate = new Date(date);
                if (isNaN(specificDate.getTime())) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: 'date' }));
                }
                startDate = new Date(Date.UTC(specificDate.getUTCFullYear(), specificDate.getUTCMonth(), specificDate.getUTCDate(), 0, 0, 0));
                endDate = new Date(Date.UTC(specificDate.getUTCFullYear(), specificDate.getUTCMonth(), specificDate.getUTCDate(), 23, 59, 59));
            }

            const [labours, availabilities] = await Promise.all([
                Labour.find({ userId: { $in: labourUserIds }, isDeleted: false })
                    .select('userId employee_id availability_status')
                    .lean(),
                Availability.find({
                    isDeleted: false,
                    userId: { $in: labourUserIds },
                    $or: [
                        { startDate: { $lte: endDate }, endDate: { $gte: startDate } },
                        { startDate: null, endDate: { $gte: startDate } },
                        { endDate: null, startDate: { $lte: endDate } },
                    ],
                })
                    .populate('userId', 'firstName lastName email')
                    .populate('jobId', 'companyName location')
                    .lean(),
            ]);

            const labourMap = new Map(labours.map(l => [l.userId.toString(), l]));
            const userMap = new Map(validLabourUsers.map(u => [u._id.toString(), u]));
            const availabilityMap = new Map<string, any[]>();
            availabilities.forEach(avail => {
                const userId = avail.userId._id.toString();
                if (!availabilityMap.has(userId)) {
                    availabilityMap.set(userId, []);
                }
                availabilityMap.get(userId)!.push(avail);
            });

            const data = { available: [] as any[], unavailable: [] as any[] };

            labourUserIds.forEach(userId => {
                const labour = labourMap.get(userId);
                const user = userMap.get(userId);
                const availabilityRecords = availabilityMap.get(userId) || [];
                let effectiveStartDate = startDate;
                let effectiveEndDate = endDate;
                let isDefault = true;
                let jobId: string | undefined;
                let companyName: string | undefined;
                let location: string | undefined;
                let holidayNotes: string | undefined

                let hasUnavailablePeriod = false;
                for (const avail of availabilityRecords) {
                    if (avail.status.toLowerCase() === AvailableStatusEnum.UNAVAILABLE.toLowerCase()) {
                        const availStart = avail.startDate ? new Date(avail.startDate) : new Date(0);
                        const availEnd = avail.endDate ? new Date(avail.endDate) : new Date(8640000000000000);

                        if (availStart <= endDate && availEnd >= startDate) {
                            hasUnavailablePeriod = true;
                            if (availStart > startDate && availStart < effectiveEndDate) {
                                effectiveStartDate = availStart;
                            }
                            if (availEnd < endDate && availEnd > effectiveStartDate) {
                                effectiveEndDate = availEnd;
                            } else if (availEnd >= endDate) {
                                effectiveEndDate = endDate;
                            }
                            isDefault = false;
                            jobId = avail.jobId?._id.toString();
                            companyName = (avail.jobId as any)?.companyName;
                            location = (avail.jobId as any)?.location;
                            holidayNotes = avail.holidayNotes
                            break;
                        }
                    }
                }

                const labourer = {
                    id: labour?._id.toString() || userId,
                    firstName: user?.firstName || 'Unknown',
                    lastName: user?.lastName || 'Labour',
                    email: user?.email || '',
                    employee_id: labour?.employee_id || '',
                    status: hasUnavailablePeriod ? AvailableStatusEnum.UNAVAILABLE : AvailableStatusEnum.AVAILABLE,
                    startDate: effectiveStartDate.toISOString(),
                    endDate: effectiveEndDate.toISOString(),
                    isDefault,
                    location,
                    companyName,
                    phone: user?.phone,
                    countryCode: user?.countryCode ?? null,
                    holidayNotes

                };

                if (hasUnavailablePeriod) {
                    data.unavailable.push(labourer);
                } else {
                    data.available.push(labourer);
                }
            });

            if (availableStatus) {
                if (availableStatus.toLowerCase() === AvailableStatusEnum.AVAILABLE.toLowerCase()) {
                    data.unavailable = [];
                } else if (availableStatus.toLowerCase() === AvailableStatusEnum.UNAVAILABLE.toLowerCase()) {
                    data.available = [];
                }
            }

            return {
                data,
                availableCount: data.available.length,
                unavailableCount: data.unavailable.length
            };
        } catch (error) {
            console.error('Error in getCalendarData:', error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: 'Calendar data' }));
        }
    }



    public async getUserCalendarData(
        userId: string,
        startDate: string | undefined,
        endDate: string | undefined,
        t: TFunction
    ) {
        try {
            const user = await User.findOne({ _id: userId, isDeleted: false })
                .populate({
                    path: 'roleId',
                    match: { name: { $regex: `^${RolesEnum.LABOUR.toLowerCase()}$`, $options: 'i' } },
                    select: 'name',
                })
                .select('_id firstName lastName email phone countryCode')
                .lean();

            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: 'Labour user' }));
            }

            const labour = await Labour.findOne({ userId, isDeleted: false }).select('userId employee_id').lean();

            let dateFilter: any = {};
            if (startDate && endDate) {
                const start = new Date(startDate);
                const end = new Date(endDate);
                if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: 'startDate or endDate' }));
                }
                // const startUTC = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 0, 0, 0));
                // const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 23, 59, 59));
                dateFilter = {
                    $or: [
                        { startDate: { $lte: startDate }, endDate: { $gte: endDate } },
                        { startDate: null, endDate: { $gte: startDate } },
                        { endDate: null, startDate: { $lte: endDate } },
                    ],
                };
            }

            const availabilities = await Availability.find({
                userId,
                isDeleted: false,
                status: { $in: [AvailableStatusEnum.UNAVAILABLE, AvailableStatusEnum.HOLIDAY] },
                ...dateFilter,
            })
                .populate('jobId', 'companyName location')
                .lean();
            const data = availabilities.map(avail => ({
                _id: avail._id,
                userId: userId,
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                email: user?.email || '',
                jobId: avail.jobId,
                status: avail.status,
                startDate: avail.startDate?.toISOString() || null,
                endDate: avail.endDate?.toISOString() || null,
                holidayNotes: avail.holidayNotes || null,
                companyName: (avail.jobId as any)?.companyName,
                location: (avail.jobId as any)?.location,
            }));

            return {
                data,
            };
        } catch (error) {
            console.error('Error in getUserCalendarData:', error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: 'User calendar data' }));
        }
    }


}
